import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, Chip, IconButton, TextField, InputAdornment, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Term = ({ label, children }) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography component="span" sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#c8c0e8' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.73rem', color: 'rgba(232,228,248,0.55)', mt: 0.2, lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Box>
);

const P = ({ children, sx }) => (
  <Typography sx={{ fontSize: '0.73rem', color: 'rgba(232,228,248,0.55)', lineHeight: 1.6, mb: 1.2, ...sx }}>
    {children}
  </Typography>
);

const SubHead = ({ color = '#a78bfa', children }) => (
  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, mb: 0.5,
                    textTransform: 'uppercase', letterSpacing: 0.3 }}>
    {children}
  </Typography>
);

/* ── section content ─────────────────────────────────────── */

const SECTIONS = [
  {
    id: 'overview',
    title: 'What This Tool Does',
    keywords: 'phases scan ct log rdap dns permutations keyword combos classification discovery',
    body: (
      <>
        <P>
          DomainProtection detects domains that impersonate your brand. It finds typosquatting,
          look-alike domains, phishing sites, and TLD variations that could be used to
          trick your users or customers.
        </P>
        <P>For each monitored domain, a deep scan runs these phases in order:</P>
        <Box component="ol" sx={{ pl: 2, mt: 1, mb: 0, '& li': { fontSize: '0.73rem', color: 'rgba(232,228,248,0.55)', mb: 0.8, lineHeight: 1.5 } }}>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 1: CT Log Search</strong>: queries Certificate Transparency history (crt.sh over HTTP, then crt.sh's public database when the web endpoint is down) for any certificate issued containing your brand name. Fast, passive lookup.</li>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 2: RDAP Registration Check</strong>: two sweeps: (A) checks your exact brand across 137 curated high-abuse TLDs via RDAP to find registered domains even if they have no DNS or certificates, then (B) checks close misspellings and brand+keyword combos via RDAP. This is the slowest phase due to RDAP rate limits (1-5s per query).</li>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 3: DNS TLD Sweep</strong>: resolves your exact brand name across all ~1,500 IANA TLDs via DNS to catch registrations on TLDs not covered by RDAP. Automatically detects and skips wildcard TLDs that resolve everything. Very fast (5-15 seconds).</li>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 4: DNS Permutations</strong>: runs dnstwist plus an internal generator to produce hundreds of typo, homoglyph, and TLD variations, then mass-resolves them via DNS to find which are registered and live. Very fast (mass parallel DNS resolution).</li>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 5: Keyword Combos</strong>: generates brand+keyword patterns (e.g. brand-login.com, brand-billing.net) across 61 TLDs and DNS-resolves them. Capped at 100,000 combos per scan to keep timing predictable.</li>
          <li><strong style={{ color: '#c8c0e8' }}>Phase 6: Classification</strong>: visits each newly detected domain over HTTP and classifies it as active, parked, for sale, redirect, unclassified, or inactive. Also checks MX records and threat intelligence databases.</li>
        </Box>
        <P sx={{ mt: 1 }}>
          Phases 1-5 are discovery (finding candidate domains). Phase 6 is analysis
          (determining what each domain is doing). All phases run per monitored domain
          before moving to the next domain.
        </P>
      </>
    ),
  },
  {
    id: 'channels',
    title: 'Continuous Monitoring',
    keywords: 'ct stream nrd real-time certstream newly registered reclassify live monitor certs per sec',
    body: (
      <>
        <P>
          Deep scans are only one of three discovery channels. The other two run
          continuously and appear in the Live Monitor panel:
        </P>
        <Term label="CT stream (real-time)">
          A self-hosted Certificate Transparency watcher follows the four major non-Google
          log operators (Let's Encrypt, DigiCert, Sectigo, Cloudflare) and matches every
          newly logged certificate against your brands within minutes of issuance. Detections
          from this channel carry a green source dot and a "CT stream" origin.
        </Term>
        <Term label="NRD watcher (daily)">
          Pulls the free daily newly-registered-domains list and matches every entry against
          your brands. This catches lookalikes the day they are REGISTERED, before any
          certificate exists or a site goes live. The free list covers roughly 70,000
          domains/day (under 10% of global registrations), so treat it as early warning,
          not full coverage.
        </Term>
        <Term label="Re-classification (every few hours)">
          Existing detections are re-checked automatically: liveness, content similarity,
          threat intel, and score. A parked lookalike that goes live is caught within hours,
          not at the next scan.
        </Term>
        <P>
          The channels deliberately overlap: the stream is real-time but cert-only, the NRD
          watcher sees registrations before certificates, and the deep scan is the
          comprehensive backstop that also generates candidate domains itself.
        </P>
      </>
    ),
  },
  {
    id: 'risk-scores',
    title: 'Risk Scores',
    keywords: 'score points critical high medium low similarity intent classification mx cert breakdown factors match class exact contains typo look-alike evidence gate tld parked',
    body: (
      <>
        <P>
          Each domain gets a risk score from 0-100. Additive factors build a raw score, then
          an evidence gate floors or caps it so the band reflects corroborating evidence, not
          accumulated points. Hover the score for the factor list, or expand the row: the
          detail panel shows factor bars plus a "Why this rank" line when a gate applied.
        </P>
        <P sx={{ mb: 0.5 }}>
          Scoring prioritises what a domain can <em>do</em> (serve phishing pages, send BEC
          emails) over how it was detected. A boring keyword domain that is active with MX
          records scores higher than a clever homoglyph that is parked.
        </P>
        <Term label="Match Class (chip on each row)">
          How the name relates to your brand. EXACT: your brand on another TLD or subdomain.
          CONTAINS: your brand embedded in a longer name (combosquat). TYPO: a plausible
          keyboard or visual typo. LOOK-ALIKE: resembles the brand but the edits are not
          plausible typos, usually a different business; kept low unless real evidence appears.
        </Term>
        <Term label="Similarity (0-30 pts)">
          Name closeness, scaled by match class: exact scores 30, contained and plausible
          typos are floored high, look-alikes are capped at 12 no matter how close the raw
          edit distance looks.
        </Term>
        <Term label="Classification (0-25 pts)">
          What the domain is doing right now. Active sites serving real content score 25:
          the single largest factor. Redirects score 15, for-sale 5, parked 2, inactive 0.
          Parking is detected by infrastructure, not just page text: known parking
          nameservers/IPs and wildcard-DNS artifacts are classified parked even when they
          serve real-looking HTML.
        </Term>
        <Term label="Intent (0-15 pts)">
          What the domain name implies: credential theft keywords (login, portal, auth) and
          financial fraud keywords (eft, billing, payment) score 15. Email infrastructure
          (mail, webmail) scores 14. Urgency/impersonation lures (breach, hipaa, urgent)
          score 13. Generic keywords (web, cloud) score 3. Typo/lookalike techniques score
          a flat 10 since intent is unknown until classified.
        </Term>
        <Term label="Email Infrastructure (0-15 pts)">
          Tiered scoring based on email capability. MX records alone (can receive email) score 10 pts.
          MX + SPF or DMARC (active sending infrastructure) scores 15 pts, indicating a fully configured
          BEC-capable domain. Marked with yellow "MX", "SPF", and "DMARC" badges.
        </Term>
        <Term label="Content Match (0-15 pts)">
          The site visually matches your brand: serving your exact favicon scores 15,
          a near-identical page layout (screenshot perceptual hash) 12, a matching page
          title 8. Marked with the red "CLONE" badge.
        </Term>
        <Term label="Certificate Age (0-10 pts)">
          Recently issued SSL certificates suggest the domain was just set up for a campaign.
          (The issuer itself is not scored: free CAs sign benign and malicious sites alike.)
        </Term>
        <Term label="Domain Age (-8 to +10 pts)">
          Fresh registrations boost the score. For LOOK-ALIKE names only, old age subtracts:
          a neighbor-looking name still live after years with zero evidence is almost
          certainly a different business. Brand-carrying names are never discounted for age.
        </Term>
        <Term label="TLD Risk (+3 pts)">
          Small boost for cheap, abuse-heavy TLDs (.xyz, .top, .icu, .site and similar).
        </Term>
        <Term label="Threat Intel (up to +20 pts)">
          Domains found in public abuse databases (PhishDestroy, PhishTank,
          Phishing.Database) score by consensus: more independent sources and fresher
          listings score higher. Marked with a red "THREAT INTEL" badge.
        </Term>
        <Term label="Popularity (negative)">
          Domains in the Majestic Million top list get a score reduction: famous domains that
          merely resemble your brand are usually not impersonation.
        </Term>
        <Term label="Evidence Gate (floor / cap)">
          The final overlay, shown as its own bar in the breakdown. CRITICAL (90+) requires
          confirmed impersonation evidence: a content clone, threat-intel consensus, or a
          malicious urlscan verdict. HIGH (70+) requires evidence or a brand-carrying name
          that is live or mail-capable. Your exact brand on an unowned TLD that is live or
          has MX floors at 75. Parked domains carrying your brand stay visible in MEDIUM
          (they can be un-parked overnight); parked look-alikes and wildcard artifacts drop
          to LOW. The applied rule appears as "Why this rank" in the row detail.
        </Term>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {[
            { label: 'CRITICAL 90+', bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
            { label: 'HIGH 70+', bg: 'rgba(251,146,60,0.15)', color: '#fb923c' },
            { label: 'MEDIUM 40+', bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
            { label: 'LOW 0+', bg: 'rgba(74,222,128,0.12)', color: '#4ade80' },
          ].map(r => (
            <Chip key={r.label} label={r.label} size="small"
              sx={{ bgcolor: r.bg, color: r.color, fontWeight: 700, fontSize: '0.62rem' }} />
          ))}
        </Box>
      </>
    ),
  },
  {
    id: 'detection-types',
    title: 'Detection Types',
    keywords: 'fuzzer dnstwist homoglyph bitsquatting omission keyword combos subdomain tld variation typosquat idn',
    body: (
      <>
        <P>
          Each finding is tagged with how it was detected. Types are color-coded by source:
        </P>
        <Box sx={{ mb: 2.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2,
                   border: '1px solid rgba(255,255,255,0.05)' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Chip label="example" size="small"
              sx={{ bgcolor: 'rgba(248,113,113,0.15)', color: '#f87171', fontSize: '0.65rem' }} />
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.45)' }}>
              CT log search / internal generator
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Chip label="example" size="small"
              sx={{ bgcolor: 'rgba(124,92,191,0.18)', color: '#a78bfa', fontSize: '0.65rem' }} />
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.45)' }}>
              dnstwist permutation engine
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label="example" size="small"
              sx={{ bgcolor: 'rgba(251,146,60,0.15)', color: '#fb923c', fontSize: '0.65rem' }} />
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.45)' }}>
              brand + keyword combo
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SubHead color="#f87171">Phase 1: CT Log Search</SubHead>
          <P>
            Certificate Transparency is a public system where every SSL/TLS certificate issued by a
            Certificate Authority (CA) must be logged. When someone registers a domain and gets an SSL
            certificate for it, that certificate appears in public CT logs. This scan queries crt.sh
            (a CT log search engine) for any certificate containing your brand name, catching domains
            before they even go live. The real-time CT stream uses the same matching, tagged
            "ctstream-" in the detection type.
          </P>
          <Term label="associated">
            Any domain found in CT logs containing your brand name that doesn't fit a more specific category.
            Example: "my-acmecorp-portal.com" found in a certificate log.
          </Term>
          <Term label="tld variation">
            Your exact brand name registered under a different top-level domain.
            Example: acmecorp.net, acmecorp.org. Attackers register these to catch users who mistype the extension.
          </Term>
          <Term label="hyphenation">
            Your brand name split with hyphens at word boundaries and found in CT logs.
            Example: "acme-corp.com" for "acmecorp.com". Often registered by squatters or competitors.
          </Term>
          <Term label="subdomain">
            A legitimate subdomain of your own domain found in certificate logs.
            Example: "staging.acmecorp.com". Usually benign but worth verifying you own it.
          </Term>
          <Term label="subdomain abuse">
            Your brand used as a subdomain on someone else's domain to look legitimate.
            Example: "acmecorp.com.login-secure.net". A common phishing technique.
          </Term>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SubHead>Phase 2: RDAP Registration Check</SubHead>
          <P>
            RDAP (Registration Data Access Protocol) checks if domains are registered even when they have no DNS
            records and no SSL certificates. This catches pre-attack staging domains that CT logs and DNS scanning
            miss entirely.
          </P>
          <Term label="rdap-exact-tld">
            Your exact brand name registered on a different TLD. High squatting signal since there is no
            legitimate reason for someone else to register your brand on a new extension.
          </Term>
          <Term label="rdap-new-registration">
            A brand permutation recently registered but not yet serving content. May indicate a domain
            being staged for a future phishing campaign.
          </Term>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SubHead>Phase 4: dnstwist DNS Permutations</SubHead>
          <P>
            dnstwist is a domain permutation engine. It generates hundreds of variations of your domain
            using different techniques (typos, look-alike characters, bit-flips, etc.), then checks DNS
            to see which ones are actually registered and resolve to an IP address. Only live, registered
            domains appear in results.
          </P>
          <Term label="homoglyph">
            Characters replaced with visually identical ones from other alphabets. Cyrillic "a" (U+0430)
            looks identical to Latin "a" but is a different character. Also catches "rn" looking like "m",
            "cl" looking like "d", etc. These are the hardest to spot and highest risk.
          </Term>
          <Term label="bitsquatting">
            A domain caused by a single bit-flip in memory. If one bit changes in "acmecorp" while
            stored in RAM, it could become "acmecorq". This actually happens due to hardware errors
            and cosmic rays: enough traffic makes it statistically significant.
          </Term>
          <Term label="omission">
            One character removed from your domain. Example: "acmecop.com" (missing the "r").
            The most common accidental typo users make.
          </Term>
          <Term label="repetition">
            A character doubled. Example: "acmeecorp.com" or "acmecorpp.com".
            Happens when a user accidentally holds a key too long.
          </Term>
          <Term label="replacement">
            A character replaced with an adjacent key on the keyboard.
            Example: "acmecirp.com" where "o" replaced by "i". Catches fat-finger typos.
          </Term>
          <Term label="transposition">
            Two adjacent characters swapped. Example: "acmecrop.com" swapping "or" to "ro".
            A very common typing mistake, especially at speed.
          </Term>
          <Term label="insertion">
            An extra character inserted in the middle of the domain.
            Example: "acmecorrp.com". Catches double-tap keyboard errors.
          </Term>
          <Term label="addition">
            A character appended to the end. Example: "acmecorps.com" or "acmecorpa.com".
            Catches overshoot typos.
          </Term>
          <Term label="vowel-swap">
            Vowels exchanged for other vowels. Example: "acmacorp.com" (e to a).
            Catches common spelling confusion.
          </Term>
          <Term label="hyphenation">
            Hyphens inserted at positions within the domain. Example: "acme-corp.com".
            Generated by dnstwist's permutation engine and DNS-verified.
          </Term>
          <Term label="subdomain">
            Your domain broken into a fake subdomain structure. Example: "a.cmecorp.com"
            where the first character is split off. Makes the domain look like a subdomain of a
            different site.
          </Term>
          <Term label="tld-swap">
            Your domain with a different TLD. Example: acmecorp.co, acmecorp.io.
            Generated by dnstwist and verified via DNS resolution.
          </Term>
          <Term label="cyrillic">
            Entire domain replaced with Cyrillic look-alike characters. Unlike single-character
            homoglyphs, this replaces the whole domain with the Cyrillic alphabet equivalent.
            All punycode/IDN domains are decoded and analyzed via skeleton matching to detect
            cross-script homograph attacks, rather than being skipped.
          </Term>
          <Term label="plural">
            Singular/plural variation. Example: "acmecorps.com" or a de-pluralized form.
          </Term>
          <Term label="dictionary">
            A common English word prepended or appended to your domain.
            Example: "myacmecorp.com" or "acmecorponline.com".
          </Term>
          <Term label="various">
            Other character-level permutations generated by dnstwist that don't fit the above categories.
          </Term>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SubHead color="#f87171">Phase 4 Supplement: Internal Generator</SubHead>
          <P>
            Our own permutation generator supplements dnstwist with additional variations that
            dnstwist may miss, including Unicode homoglyphs and broader TLD coverage.
            These are also DNS-verified before appearing in results.
          </P>
          <Term label="typosquatting">
            Keyboard-proximity substitutions, character omission, character doubling, and adjacent-key
            swaps generated by our own engine. Covers some patterns dnstwist doesn't, particularly
            for shorter domain names.
          </Term>
          <Term label="homoglyph">
            Unicode look-alike character substitutions from our extended homoglyph database, including
            accented Latin characters (e.g. a, e, o) and Cyrillic equivalents.
          </Term>
        </Box>

        <Box sx={{ mb: 1 }}>
          <SubHead color="#fb923c">Phase 5: Brand + Keyword Combos</SubHead>
          <P>
            This phase generates domains by combining your brand name with common phishing and
            corporate keywords, then checks DNS to find which ones are registered. These domains
            target users who search for your services or click links in phishing emails.
            Categories include: authentication (login, signin, sso), billing/payment (pay, invoice,
            eft, remittance), security lures (secure, verify, alert, urgent), support/helpdesk,
            email (mail, webmail), portals (portal, admin, dashboard, providerportal, memberportal,
            claimsportal), accounts, updates, corporate identity, healthcare/insurance (patient,
            claims, hipaa, benefits, openenrollment), regulatory impersonation (cms, hitrust, oig),
            AI/healthtech, product names, breach-notification lures, recruitment, legal, investor,
            vendor/partner, training, survey, IT infrastructure (okta, vpn, sharepoint), and
            community terms. Hover any detection-type chip in the table for its exact meaning.
          </P>
        </Box>
      </>
    ),
  },
  {
    id: 'classifications',
    title: 'Classifications',
    keywords: 'active parked for sale redirect unclassified inactive label http classify',
    body: (
      <>
        <P>
          After detection, each domain is visited over HTTP and automatically classified. Hover over any classification chip to see the reason.
        </P>
        <Term label="Active">
          Serves real web content with no parking or sale indicators. Highest priority to investigate: could be a phishing site.
        </Term>
        <Term label="For Sale">
          Listed on a domain marketplace (GoDaddy, Sedo, HugeDomains, etc.). Consider purchasing to protect your brand.
        </Term>
        <Term label="Redirect">
          Redirects to a different registered domain. Check where it points: could be benign or malicious.
        </Term>
        <Term label="Parked">
          Placeholder page from a registrar or hosting provider. Low immediate risk but worth monitoring.
        </Term>
        <Term label="Unclassified">
          DNS resolves (domain is registered and live) but the website was unreachable: blocked
          by a web filter, SSL error, connection refused, or timed out. Also shown for domains
          with MX records but no website (email-only, a BEC staging risk). Treat these as
          live domains requiring manual investigation, not as inactive.
        </Term>
        <Term label="Inactive">
          DNS does not resolve. The domain is not currently registered or has no active DNS.
          Not a threat now but may become active: monitored on future scans.
        </Term>
      </>
    ),
  },
  {
    id: 'scan-reliability',
    title: 'Partial Scans & Source Health',
    keywords: 'partial crt.sh breaker circuit outage degraded amber chip probe unreachable phase failed',
    body: (
      <>
        <P>
          An amber <b style={{ color: '#fbbf24' }}>Partial</b> chip on a scan means at least one
          discovery source failed during that run, so the results may be incomplete for that
          source. The scan still finishes and everything else still counts. Hover the chip for
          exactly which phases failed and for which domain.
        </P>
        <Term label="Why this happens (usually crt.sh)">
          crt.sh, the public CT search engine used by Phase 1, is chronically overloaded. Every
          scan starts with a health probe; when crt.sh is down, a circuit breaker makes the CT
          phase fail fast instead of stalling the scan for minutes. The Live Monitor shows the
          current probe result on the crt.sh tile.
        </Term>
        <Term label="What covers the gap">
          The real-time CT stream is independent of crt.sh and keeps watching certificates the
          whole time. A crt.sh outage during a scan mostly affects deep-history lookups, not
          real-time coverage.
        </Term>
        <Term label="What to do">
          Nothing, usually. Partial is an honesty flag, not an error: absence of findings in a
          failed phase is not evidence of absence. If crt.sh stays down for days, expect Partial
          chips on every scan until it recovers.
        </Term>
      </>
    ),
  },
  {
    id: 'triage',
    title: 'Review Workflow & Allowlist',
    keywords: 'triage status new reviewed false positive allowlist suppress known good quick actions',
    body: (
      <>
        <P>
          Every finding starts as <b>New</b>. Use the hover actions on a row (or the buttons in
          the expanded detail) to move it through review:
        </P>
        <Term label="Reviewed">
          A human looked at it and it stays on the radar. It keeps its score and keeps being
          re-checked; the chip just records that it has been seen.
        </Term>
        <Term label="False positive">
          Not an impersonation (or not worth tracking). The row stays as data, shown dimmed and
          struck through, and is excluded from the Live Monitor ticker. The domain can still
          re-appear on future discovery if it is not allowlisted.
        </Term>
        <Term label="Allowlist (never alert again)">
          For recurring legitimate neighbors: a real company one letter away from your brand
          will re-alert every time it issues a certificate. Allowlisting the domain suppresses
          it permanently across every discovery path (scans, NRD, CT stream, urlscan hunts),
          covers all of its subdomains, and marks existing matching rows false positive.
          Manage entries via the shield icon in the table header. Removing an entry re-enables
          future detections but does not un-suppress old rows.
        </Term>
        <Term label="Auto-suppress">
          The triage API can also bulk-mark obviously inert rows (parked/inactive, low risk,
          no MX, no threat-intel flag, no content match) as false positives; it runs as a
          dry-run first.
        </Term>
      </>
    ),
  },
  {
    id: 'scan-history',
    title: 'Scan History',
    keywords: 'history filter click completed auto manual nrd schedule',
    body: (
      <P sx={{ mb: 0 }}>
        Click any completed scan in the history panel to filter the alerts table to just that scan's
        results. Click again or use the clear chip to show all findings. Scans can be triggered
        manually or run automatically on a schedule configured in the sidebar; NRD rows are the
        daily registration sweeps.
      </P>
    ),
  },
  {
    id: 'filters',
    title: 'Filters, Columns & Export',
    keywords: 'filter search sort csv export columns density status dropdown',
    body: (
      <P sx={{ mb: 0 }}>
        Use the filter dropdowns to narrow results by risk level, review status, classification,
        monitored domain, or detection type. Click any column header to sort. The toolbar toggles
        row density and column visibility (both remembered per browser). The CSV export respects
        your current filters so you can export exactly what you see.
      </P>
    ),
  },
  {
    id: 'threat-intel',
    title: 'Threat Intelligence',
    keywords: 'phishdestroy phishtank phishing database abuse databases flagged badge mx spf dmarc reclassify',
    body: (
      <>
        <P>
          High-risk and active domains are automatically checked against free public abuse databases
          during each scan. Domains found in these databases are flagged with a red "THREAT INTEL" badge
          and receive an automatic risk score escalation of +20 points.
        </P>
        <Term label="PhishDestroy">
          A free phishing and scam domain database with 834K+ entries. Checks are automatic
          and require no API key. MIT-licensed, powered by destroy.tools.
        </Term>
        <Term label="PhishTank (cached feed)">
          When enabled, the PhishTank bulk feed is pulled on a shared schedule and checked
          locally, adding a third consensus source.
        </Term>
        <Term label="Phishing.Database (cached feed)">
          When enabled, the MIT-licensed Phishing.Database ACTIVE-domains list (~500K
          entries) is pulled on the same shared schedule. Unlike URL-level feeds it is
          domain-granular, so typosquat domains can match it directly.
        </Term>
        <Term label="Email Infrastructure Detection">
          Domains are checked for MX records, SPF (TXT "v=spf1"), and DMARC (_dmarc TXT "v=DMARC1")
          during classification. MX-enabled domains get a yellow "MX" badge; SPF and DMARC get their
          own badges. Risk scoring is tiered: MX only = +10 pts, MX with SPF or DMARC = +15 pts.
          Hovering the MX badge shows the actual mail server hostnames for analyst triage.
        </Term>
        <Term label="Reclassification">
          Runs automatically every few hours, and on demand via "Reclassify All Domains" in Scan
          Settings: re-checks liveness, content, threat intel, and score without a full scan.
        </Term>
      </>
    ),
  },
  {
    id: 'industry',
    title: 'Industry Tailoring',
    keywords: 'healthcare insurance payer provider portal benefits enrollment hipaa cms analytics keywords tld',
    body: (
      <>
        <P>
          This tool includes keyword categories tailored for healthcare, insurance, and data analytics
          organizations. Healthcare is the most breached industry (Verizon DBIR), with BEC attacks
          averaging $4.67M per incident. The following design decisions reflect that threat landscape:
        </P>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: 0.5, color: 'rgba(167,139,250,0.6)', mt: 2, mb: 1 }}>
          Keyword Categories (30 Categories, 368 Keywords)
        </Typography>
        <Term label="Payer/Provider Portals (15 pts intent)">
          Portal impersonation terms scored high because they directly mimic the login pages
          that payer staff, providers, and members use daily: providerportal, memberportal,
          claimsportal, eligibility, preauth, priorauth, remittance, eob, adjudication.
          The #1 phishing target for healthcare organizations.
        </Term>
        <Term label="Benefits & Enrollment (15 pts intent)">
          Open enrollment and benefits terms exploited in seasonal phishing surges:
          openenrollment, mybenefits, benefitsportal, planselection, deductible,
          copay, formulary, wellness, hsa, fsa. Attacks spike during Q4 open enrollment periods.
        </Term>
        <Term label="Payment/EFT (15 pts intent)">
          EFT and payment redirect terms targeting payment integrity and claims processing:
          eft, remittance, era, edi, claimspayment, paymentportal, providereft. Particularly
          dangerous: successful EFT redirect attacks cause immediate financial loss.
        </Term>
        <Term label="HIPAA/Compliance Lures (13 pts intent)">
          Scare-tactic terms that exploit regulatory fear: hipaanotice, compliance, audit,
          breach, incidentreport, privacynotice, breachnotification. Urgency-driven phishing
          has the highest click-through rate.
        </Term>
        <Term label="Regulatory/Government (13 pts intent)">
          Government and regulatory body impersonation: hipaa, cms, hitrust, oig, ocr, hitech,
          macra, mips, hedis, ncqa. "CMS audit notice" and "HIPAA violation alert" emails have
          extremely high click-through rates.
        </Term>
        <Term label="Breach Notification Lures (13 pts intent)">
          Data breach notification impersonation: breachnotice, dataincident, identityprotection,
          creditmonitoring, settlementclaim, phidata. Attackers exploit real breach events to
          phish affected individuals.
        </Term>
        <Term label="Healthcare / Analytics / AI (8 pts intent)">
          Patient-facing, clinical, data-platform, and AI terms: patient, records, ehr, claims,
          rx, pharmacy, provider, payer, analytics, insights, predictive, aiplatform, healthai,
          genai, copilot. Targets credential harvesting against clinical staff, member portals,
          and data platform users.
        </Term>
        <Term label="Product Brand (15 pts intent)">
          Your known product names that attackers would impersonate to create fake login pages.
          Configure product-specific keywords in the keyword generator settings.
        </Term>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: 0.5, color: 'rgba(167,139,250,0.6)', mt: 2.5, mb: 1 }}>
          TLD Coverage (61 TLDs)
        </Typography>
        <Term label="Healthcare TLDs">
          .health, .care, .clinic, .insurance, .medical, .claims, .healthcare, .life,
          .dental, .hospital, .pharmacy, .doctor, .nursing, .rehab, .wellness: attackers
          register lookalikes on industry TLDs to appear legitimate to clinicians and patients.
        </Term>
        <Term label="Technology TLDs">
          .ai, .data, .tech, .cloud, .digital: critical for AI-focused companies,
          where brand-name.ai or product-name.ai registrations appear highly credible.
        </Term>
        <Term label="General TLDs">
          .com, .net, .org, .io, .co, .info, .xyz, .online, .site, .app,
          .network, .security: standard coverage for all brand impersonation vectors.
        </Term>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: 0.5, color: 'rgba(167,139,250,0.6)', mt: 2.5, mb: 1 }}>
          Scoring & Detection Logic
        </Typography>
        <Term label="Intent-driven scoring">
          Scoring prioritises what a domain can do over how it was found. Classification
          (active/redirect/parked) is the largest single factor at 0-25 pts, because a live
          domain impersonating your brand is an active campaign.
        </Term>
        <Term label="Honest rescoring">
          Risk scores reflect current reality. When a domain is reclassified (e.g. goes from
          active to parked, or parked to active), the score is fully recalculated. A domain
          that goes offline will drop in score; one that comes alive will spike.
        </Term>
        <Term label="Brand name assumptions">
          Detection coverage is optimized for multi-syllable, compound, or invented brand
          names. Short generic dictionary words generate disproportionate noise and should
          not be monitored.
        </Term>
      </>
    ),
  },
  {
    id: 'coverage',
    title: 'What We Might Miss',
    keywords: 'blind spots coverage gaps miss dormant cctld idn punycode lag limits',
    body: (
      <>
        <P>
          No scanning tool can guarantee 100% detection of every impersonating domain. Understanding
          the blind spots helps you layer additional monitoring where it matters most:
        </P>
        <Term label="Unrelated-word lookalikes">
          If an attacker registers a domain that sounds similar but uses completely different words
          (a synonym, abbreviation, or phonetic spelling with no character overlap), the permutation
          engine will not generate it. We check typos, homoglyphs, and character swaps, not semantic
          or phonetic equivalents.
        </Term>
        <Term label="Domains registered but completely dormant">
          A domain with no DNS records, no certificate, and no RDAP entry on the curated TLD list
          will not appear. An attacker could register on an obscure ccTLD outside the sweep list
          and leave it dormant until ready.
        </Term>
        <Term label="Lookalikes using a different brand fragment">
          If your brand is a compound word, an attacker could register a domain using only one half
          combined with an unrelated word; that would not match keyword or permutation patterns.
        </Term>
        <Term label="IDN homoglyphs on rare Unicode blocks">
          All punycode domains are decoded and analyzed using skeleton matching (UTS39-inspired)
          covering Cyrillic, Greek, and common diacritics. Lookalikes from uncommon Unicode blocks
          (IPA Extensions, Cherokee) may not be detected.
        </Term>
        <Term label="Certificate Transparency lag">
          Batch CT search engines can take hours to index new certificates (the live stream closes
          most of this gap). Domains that never obtain a certificate will never appear in CT
          results at all: the NRD watcher and RDAP sweeps cover that space.
        </Term>
        <Term label="Rate-limited or geo-blocked RDAP/DNS">
          RDAP servers enforce rate limits and some ccTLD registries block certain regions. During
          heavy scanning some TLDs may be skipped after repeated 429 responses.
        </Term>
        <Term label="Novel attack techniques">
          Lookalike logos on unrelated domains, social media profile squatting, mobile app
          impersonation, and non-DNS systems (blockchain domains) are outside the scope of
          DNS-based scanning.
        </Term>
        <P sx={{ mt: 1.5, fontStyle: 'italic', color: 'rgba(232,228,248,0.45)', mb: 0 }}>
          DomainProtection is a detection layer, not a complete brand protection solution. Use it
          alongside threat intelligence feeds, takedown services, email authentication (DMARC
          reporting on your own domain), and incident response procedures.
        </P>
      </>
    ),
  },
  {
    id: 'sources',
    title: 'Data Sources & Attribution',
    keywords: 'sources licence license attribution majestic iok phish.report czds crt.sh playwright open source',
    body: (
      <>
        <P>
          Every data source and component is free to use commercially, self-hosted where
          possible, and credited here where a licence asks for it. No vendor free tiers.
        </P>
        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0, '& li': { fontSize: '0.73rem', color: 'rgba(232,228,248,0.55)', mb: 0.8, lineHeight: 1.5 } }}>
          <li><strong style={{ color: '#c8c0e8' }}>Certificate Transparency logs</strong>: public by design; watched live through a self-hosted certstream-server-go.</li>
          <li><strong style={{ color: '#c8c0e8' }}>crt.sh</strong> (Sectigo): historical certificate search over HTTP, with its public database as the fallback.</li>
          <li><strong style={{ color: '#c8c0e8' }}>ICANN CZDS</strong> (optional): complete zone files for newly registered domains. Used for alerting only; never redistributed, never used for marketing.</li>
          <li><strong style={{ color: '#c8c0e8' }}>WhoisDS</strong>: free daily list of newly registered domains (capped; supplementary).</li>
          <li><strong style={{ color: '#c8c0e8' }}>PhishTank, Phishing.Database (MIT), PhishDestroy</strong>: phishing feeds, looked up locally.</li>
          <li><strong style={{ color: '#c8c0e8' }}>Popularity data: Majestic Million</strong> (Majestic, CC BY 3.0). A domain in the top million is almost never phishing.</li>
          <li><strong style={{ color: '#c8c0e8' }}>Kit signatures: IOK by phish.report</strong> (ODbL) https://phish.report/IOK. Phishing-kit indicators evaluated on rendered pages when the capture sidecar is enabled.</li>
          <li><strong style={{ color: '#c8c0e8' }}>Page capture</strong>: self-hosted Playwright (Apache-2.0) and Chromium; screenshots and rendered pages never leave your server.</li>
        </Box>
      </>
    ),
  },
];

/* ── panel ────────────────────────────────────────────────── */

function HelpPanel({ onClose, initialSection }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => ({ [initialSection || 'overview']: true }));
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!initialSection) return;
    setOpen(o => ({ ...o, [initialSection]: true }));
    const t = setTimeout(() => {
      sectionRefs.current[initialSection]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [initialSection]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? SECTIONS.filter(s => `${s.title} ${s.keywords}`.toLowerCase().includes(q))
    : SECTIONS;

  const toggle = (id) => setOpen(o => ({ ...o, [id]: !o[id] }));

  return (
    <Box sx={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: { xs: '100%', sm: 440 },
      bgcolor: '#13101f', borderLeft: '1px solid rgba(255,255,255,0.1)',
      zIndex: 1300, overflowY: 'auto', boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
      // iOS: momentum scroll + stop drags at the panel edge from chaining to
      // (and rubber-banding) the dashboard behind, which reads as "can't scroll"
      WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
      pt: 'env(safe-area-inset-top)',
      '&::-webkit-scrollbar': { width: 4 },
      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 },
    }}>
      {/* Header + search */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)',
                 position: 'sticky', top: 0, bgcolor: '#13101f', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#e8e4f8' }}>
            Help & Information
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close help panel"
            sx={{ color: 'rgba(232,228,248,0.4)', '&:hover': { color: '#e8e4f8' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <TextField
          fullWidth size="small" placeholder="Search help topics..."
          value={query} onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: 'rgba(232,228,248,0.25)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem', bgcolor: 'rgba(255,255,255,0.03)',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
              '&:hover fieldset': { borderColor: 'rgba(167,139,250,0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#a78bfa' },
            },
            '& .MuiOutlinedInput-input': { py: 0.7 },
          }}
        />
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        {/* Common name warning: always visible */}
        <Box sx={{
          mb: 2, p: 1.5, borderRadius: 2,
          bgcolor: 'rgba(248,113,113,0.07)',
          border: '1px solid rgba(248,113,113,0.25)',
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f87171', mb: 0.5,
                            textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Do Not Monitor Generic or Common Words
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.5)', lineHeight: 1.6 }}>
            Avoid adding domains whose brand name is a short, common English word. Phase 5
            generates keyword combinations across all brand variants: for a unique multi-syllable
            name this produces ~300-2,000 candidates, but for a single common word it can exceed
            28,000, causing scans to run for 30-60+ minutes. This tool is designed for
            distinctive brand names, not dictionary words.
          </Typography>
        </Box>

        {visible.length === 0 && (
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(232,228,248,0.3)', textAlign: 'center', py: 3 }}>
            No help topics match "{query}"
          </Typography>
        )}

        {visible.map((section) => {
          const expanded = q ? true : !!open[section.id];
          return (
            <Box key={section.id}
              ref={(el) => { sectionRefs.current[section.id] = el; }}
              sx={{ mb: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 1,
                    scrollMarginTop: 110 }}>
              <Box
                onClick={() => !q && toggle(section.id)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: q ? 'default' : 'pointer', py: 0.8, borderRadius: 1,
                  '&:hover': q ? {} : { bgcolor: 'rgba(255,255,255,0.02)' },
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#e8e4f8',
                                  textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {section.title}
                </Typography>
                {!q && (
                  <KeyboardArrowDownIcon sx={{
                    fontSize: 17, color: 'rgba(232,228,248,0.3)',
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.18s ease',
                  }} />
                )}
              </Box>
              <Collapse in={expanded}>
                <Box sx={{ pt: 0.5, pb: 1 }}>
                  {section.body}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default HelpPanel;
